import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { suApiSlice } from 'src/redux/slices/api/su';
import { useAppDispatch, useAppSelector } from 'src/redux/hooks';
import { LearningModulesCarousel } from 'src/components/carousel';
import { LearningModuleQueryType } from 'src/types/api/learningModule';
import { useClientId, useSelLocale } from 'src/contexts/ClientContext';
import { KatSpinner, KatPopover } from '@amzn/katal-react';
import { SuIcon } from '@amzn/seller-university-ui-components';
import ClientLogger from 'src/utils/logger';
import { ClientLog } from 'src/constants/clientLog';
import ErrorView from 'src/components/ErrorView';
import { selectIsWeblabInT1 } from 'src/redux/slices/weblabs';
import { WeblabId } from 'src/constants/weblab';

interface SubtopicsSectionProps {
  topicId: string;
  subTopicList: Array<{
    subtopicId: string;
    title: string;
    description?: string;
  }>;
  registerElement?: (id: string, element: HTMLElement | null) => void;
  onSubtopicsResolved?: (subtopicIds: string[]) => void;
}

const SubtopicsSection: React.FC<SubtopicsSectionProps> = ({
  topicId,
  subTopicList,
  registerElement,
  onSubtopicsResolved,
}) => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const clientId = useClientId();
  const selLocale = useSelLocale();
  const isPolyMigration = useAppSelector((state) => selectIsWeblabInT1(state, WeblabId.NalandaPolyMigration));
  const [isLoadingModules, setIsLoadingModules] = useState(false);
  const [subtopicsWithModules, setSubtopicsWithModules] = useState<string[]>([]);

  useEffect(() => {
    let cancelled = false;
    const subscriptions: Array<{ unsubscribe: () => void }> = [];

    if (subTopicList?.length) {
      const loadAllModules = async () => {
        setIsLoadingModules(true);
        try {
          const queryPatternForSubtopic = (subtopicId: string) => ({
            clientId,
            selLocale,
            queryPattern: {
              queryType: LearningModuleQueryType.SubTopic,
              subtopicQuery: { subtopicId },
            },
          });

          // T1: use listModuleCards (Poly), C: use listLearningModules (NCPS)
          let subs: Array<{ unwrap: () => Promise<any>; unsubscribe: () => void }>;
          if (isPolyMigration) {
            subs = subTopicList.map((subtopic) => dispatch(
              suApiSlice.endpoints.listModuleCards.initiate(queryPatternForSubtopic(subtopic.subtopicId)),
            ));
          } else {
            subs = subTopicList.map((subtopic) => dispatch(
              suApiSlice.endpoints.listLearningModules.initiate(queryPatternForSubtopic(subtopic.subtopicId)),
            ));
          }
          subscriptions.push(...subs);

          const results = await Promise.all(subs.map((s, index) => s.unwrap().catch((err: any) => {
            if (isPolyMigration) {
              ClientLogger.warn(ClientLog.PayloadProcessingError, {
                message: 'ListModuleCards pre-filter call failed',
                subtopicId: subTopicList[index].subtopicId,
                error: err?.message || err?.status,
                url: window.location.href,
              });
            }
            return null;
          })));

          if (cancelled) return;

          const subtopicsWithData = subTopicList
            .filter((_, index) => {
              const result = results[index];
              if (!result) return false;
              // T1 response has 'cards', C response has 'modules'
              const items = isPolyMigration
                ? (result as { cards?: unknown[] }).cards
                : (result as { modules?: unknown[] }).modules;
              return items && items.length > 0;
            })
            .map((subtopic) => subtopic.subtopicId);

          setSubtopicsWithModules(subtopicsWithData);
          onSubtopicsResolved?.(subtopicsWithData);
        } finally {
          if (!cancelled) setIsLoadingModules(false);
        }
      };

      loadAllModules();
    }

    return () => {
      cancelled = true;
      subscriptions.forEach((s) => s.unsubscribe());
    };
  }, [topicId, subTopicList, dispatch, clientId, selLocale, isPolyMigration]);

  if (isLoadingModules) {
    return (
      <div className="flex min-h-[15rem] items-center justify-center">
        <KatSpinner />
      </div>
    );
  }

  if (subtopicsWithModules.length === 0) {
    if (subTopicList?.length) {
      ClientLogger.info(ClientLog.IllegalInternalDataState, {
        topicId,
        message: 'Topic has no associated learning modules',
        path: location.pathname,
      });
    }
    return <ErrorView />;
  }

  return (
    <div>
      {subTopicList
        ?.filter((subtopic) => subtopicsWithModules.includes(subtopic.subtopicId))
        .map((subtopic) => (
          <section
            key={subtopic.subtopicId}
            ref={registerElement ? (el) => registerElement(`subtopic-${subtopic.subtopicId}`, el) : undefined}
            className="mb-su-space-08"
          >
            <div className="mb-su-space-04 flex items-center gap-su-space-03">
              <h2 className="!text-su-heading-400 !font-bold">{subtopic.title}</h2>
              {subtopic.description && (
                <KatPopover
                  position="top-start"
                  triggerType="click"
                  variant="azure"
                >
                  <div slot="trigger" className="mb-su-space-02">
                    <SuIcon
                      icon="info"
                      size="xsmall"
                      className="cursor-pointer"
                    />
                  </div>
                  {subtopic.description}
                </KatPopover>
              )}
            </div>
            <LearningModulesCarousel subtopicId={subtopic.subtopicId} />
          </section>
        ))}
    </div>
  );
};

export default SubtopicsSection;
